import { useEffect, useMemo, useRef } from 'react';
import { useMap, useSetState } from 'react-use';
import keyBy from 'lodash/keyBy';
import sortBy from 'lodash/sortBy';

import { UpdateQuestionEntity, UpdateQuizQuestionConfigInput } from '@/types/schema';

import { QuizEditorDetailsFragment } from '../../graphql/fragments/generated/QuizEditorContent';
import { adaptQuestionInput } from '../../utils/adapters';
import { PublishQuizAction } from '../useQuizEditor/actions';
import { QuizEditorUiState } from '../useQuizEditor/types';

const INITIAL_UI_STATE = {
  selectedQuestionIndex: 0,
  errors: {},
  isOfSameWorkspace: true,
  isMovingQuestion: false,
  movingQuestionId: undefined,
};

type UpdatedQuestionsState = Record<string, UpdateQuestionEntity>;
type UpdatedQuizQuestionConfigsState = Record<
  string,
  Partial<Pick<UpdateQuizQuestionConfigInput, 'order' | 'sectionId'>>
>;

interface Params {
  quiz: QuizEditorDetailsFragment;
  isQuizPublished: boolean;
}

const useQuizEditorState = ({ quiz, isQuizPublished }: Params) => {
  const publishActionRef = useRef<PublishQuizAction>();
  const [uiState, setUiState] = useSetState<QuizEditorUiState>(INITIAL_UI_STATE);

  const [updatedQuestionById, updateQuestionByIdActions] = useMap<UpdatedQuestionsState>({});
  const [updatedQuizQuestionByQuestionId, updatedQuizQuestionByQuestionIdActions] =
    useMap<UpdatedQuizQuestionConfigsState>({});

  const serverQuizQuestionsSignature = useMemo(() => {
    return quiz.quizQuestions
      .map((qq) => `${qq.questionId}:${qq.order}:${qq.sectionId ?? 'null'}`)
      .join('|');
  }, [quiz.quizQuestions]);

  // Reconcile optimistic overrides with backend truth after refetch.
  // If backend didn’t persist (or changed) sectionId/order, we must snap UI back.
  useEffect(() => {
    updatedQuizQuestionByQuestionIdActions.setAll({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // updatedQuizQuestionByQuestionIdActions if added in the deps, was causing a infinite re render
  }, [quiz.id, serverQuizQuestionsSignature]);

  const publishedQuestionById: Record<
    string,
    Pick<UpdateQuestionEntity, 'options'>
  > = useMemo(() => {
    const publishedQuestionByIdMap: Record<string, Pick<UpdateQuestionEntity, 'options'>> = {};
    if (isQuizPublished) {
      quiz.questions.forEach((question) => {
        const { publishedQuestion } = question;
        if (!publishedQuestion) return;
        const questionOptions = publishedQuestion.options ?? [];
        const correctOptionsById = keyBy(publishedQuestion.correctOptions, 'optionId');
        const options = questionOptions.map((option) => ({
          ...option,
          optionEvaluation: Boolean(correctOptionsById[option.optionId]),
        }));
        publishedQuestionByIdMap[question.id] = { options: { set: options } };
      });
    }

    return publishedQuestionByIdMap;
  }, [isQuizPublished, quiz.questions]);

  const questions = useMemo(() => {
    const quizQuestionsByQuestionId = keyBy(quiz.quizQuestions, 'questionId');
    const effectiveQuizQuestionsByQuestionId = {
      ...quizQuestionsByQuestionId,
      ...Object.fromEntries(
        Object.entries(updatedQuizQuestionByQuestionId).map(([questionId, override]) => [
          questionId,
          { ...(quizQuestionsByQuestionId as any)[questionId], questionId, ...override },
        ]),
      ),
    } as Record<string, { questionId: string; order?: number; sectionId?: string | null }>;

    return sortBy(
      quiz.questions.map((question) => {
        const updatedQuestion = updatedQuestionById[question.id];
        const adapted = updatedQuestion ? updatedQuestion : adaptQuestionInput(question);
        const sectionId = effectiveQuizQuestionsByQuestionId[question.id]?.sectionId;
        return sectionId ? { ...adapted, sectionId } : adapted;
      }),
      [
        (question) =>
          effectiveQuizQuestionsByQuestionId[question.id]?.order ?? quiz.questions.length,
      ],
    );
  }, [quiz.questions, quiz.quizQuestions, updatedQuestionById, updatedQuizQuestionByQuestionId]);

  const effectiveQuizQuestions = useMemo(() => {
    const quizQuestionsByQuestionId = keyBy(quiz.quizQuestions, 'questionId');
    return quiz.quizQuestions.map((qq) => ({
      ...qq,
      ...(updatedQuizQuestionByQuestionId[qq.questionId] ?? {}),
    })) as Array<
      QuizEditorDetailsFragment['quizQuestions'][number] & {
        order?: number;
        sectionId?: string | null;
      }
    >;
  }, [quiz.quizQuestions, updatedQuizQuestionByQuestionId]);

  return {
    uiState,
    publishActionRef,
    updatedQuestionById,
    questions,
    publishedQuestionById,
    effectiveQuizQuestions,
    updatedQuizQuestionByQuestionId,

    setUiState,
    updateQuestionByIdActions,
    updatedQuizQuestionByQuestionIdActions,
  };
};

export default useQuizEditorState;
